/**
 * Module-level lifecycle for the online chunk-upload event listeners.
 *
 * Attached from `useMutateStartTauriRecording` once Rust starts the session,
 * and detached from `useMutateStopRecording`.
 *
 * Listeners:
 *  - `chunk_uploaded`       → poll live transcripts (replaces the per-chunk
 *                             `pollLiveTranscripts` the FE upload path ran).
 *  - `chunk_failed`         → log (the chunk file is retained in Rust; the
 *                             backup recording uploaded at stop is the safety net).
 *  - `token_refresh_needed` → optional Redux sync. Rust force-refreshes Auth0
 *                             on 401; this listener keeps Redux in sync.
 */
import { isNoSpeechWarningMidSessionEnabled } from '@/app-shell/flags/no-speech-mid-session-warning-flag';
import { isNoSpeechWarningStartupEnabled } from '@/app-shell/flags/no-speech-startup-warning-flag';
import { pollLiveTranscripts } from '@/hooks/query/useQueryLiveTranscriptBySessionId';
import { setToken } from '@/lib/authActions';
import { desktopAuthBridge } from '@/tauri/auth0';
import { logSessionPipelineDatadogEvent } from '@/utils/analytics/log-hardware-datadog-event';
import { SESSION_SOURCE_TYPE } from '@/utils/analytics/session-source-type';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { toast } from '@heidi/design-system/toast';
import { TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY } from '@heidi/session/contracts';
import type { IntlShape } from 'react-intl';
import {
  listenOnlineChunkFailed,
  listenOnlineChunkTokenRefreshNeeded,
  listenOnlineChunkUploaded,
  type OnlineChunkUploadedEvent,
} from './events';
import { onlineChunkAuthFailureMessages } from './listeners.messages';
import { onlineChunkUploadBridge } from './session';

type UnlistenFn = () => void;

let activeDetach: UnlistenFn | null = null;
let intlRef: IntlShape | null = null;
let lastAuthFailureToastAt = 0;
let activeSessionId: string | null = null;

const AUTH_FAILURE_TOAST_COOLDOWN_MS = 30_000;

/** Set from a React hook so imperative toast copy uses react-intl / Crowdin. */
export function setOnlineChunkUploadIntl(intl: IntlShape): void {
  intlRef = intl;
}

function formatAuthFailureMessage(
  descriptor: (typeof onlineChunkAuthFailureMessages)[keyof typeof onlineChunkAuthFailureMessages]
): string {
  return intlRef?.formatMessage(descriptor) ?? descriptor.defaultMessage ?? '';
}

/** Locked at attach time. When false, the FE push is required recovery. */
let rustAuth0TokenRefreshLock = false;

/**
 * Force-refresh Auth0 and push the token to Rust after `token_refresh_needed`.
 * When the flag is OFF this unparks the worker; when ON it is optional Redux
 * sync only — we deliberately do not push a FE token to Rust (both sides read
 * the same keychain refresh token, so an `Unavailable` RT is terminal either way).
 */
async function handleTokenRefreshNeeded(): Promise<void> {
  try {
    if (rustAuth0TokenRefreshLock) {
      // Flag ON: Rust already force-refreshed — sync Redux only; do not push
      // a second refresh to the worker or hit Auth0 again.
      const res = await desktopAuthBridge.getAccessToken({ forceRefresh: false });
      const freshToken = res?.accessToken;
      if (!freshToken) {
        logger.error('[online_chunk] token refresh returned no access token');
        return;
      }
      setToken(freshToken);
      return;
    }

    // Flag OFF: force-refresh is required to unpark the worker.
    const res = await desktopAuthBridge.getAccessToken({ forceRefresh: true });
    const freshToken = res?.accessToken;
    if (!freshToken) {
      logger.error('[online_chunk] token refresh returned no access token');
      return;
    }
    // Keep Redux in sync so non-upload FE calls see the fresh token.
    setToken(freshToken);
    await onlineChunkUploadBridge.refreshToken(freshToken);
  } catch (error) {
    logger.error('[online_chunk] failed to refresh + push token', {
      rustAuth0TokenRefresh: rustAuth0TokenRefreshLock,
      error,
    });
  }
}

/** Exported for boot re-attach; delegates to flag-aware refresh logic. */
export async function pushFreshOnlineChunkToken(): Promise<void> {
  await handleTokenRefreshNeeded();
}

function showAuthFailureToast(): void {
  const now = Date.now();
  if (now - lastAuthFailureToastAt < AUTH_FAILURE_TOAST_COOLDOWN_MS) return;
  lastAuthFailureToastAt = now;

  toast({
    tone: 'destructive',
    title: formatAuthFailureMessage(onlineChunkAuthFailureMessages.title),
    description: formatAuthFailureMessage(onlineChunkAuthFailureMessages.description),
    duration: 8000,
  });
}

function handleChunkUploaded(payload: OnlineChunkUploadedEvent): void {
  const sessionId = activeSessionId;
  if (!sessionId) return;

  // Desktop-mic sessions only: Chronicle live never attaches these listeners —
  // its chunk telemetry is emitted Rust-side on `chronicle:observability` with
  // the hardware_* names. This shared-pipeline event matches iOS naming.
  logSessionPipelineDatadogEvent('native_chunk_upload_success', {
    session_id: sessionId,
    session_id_local: sessionId,
    chunk_index: payload.index,
    bytes: payload.sizeBytes,
    duration_ms: payload.durationMs,
    session_source_type: SESSION_SOURCE_TYPE.LocalRecording,
  });
}

function handleChunkFailed(payload: {
  index: number;
  errorCode?: string;
  message: string;
}): void {
  logger.warn('[online_chunk] chunk upload failed', payload);

  if (activeSessionId) {
    logSessionPipelineDatadogEvent(
      'native_chunk_upload_failure',
      {
        session_id: activeSessionId,
        session_id_local: activeSessionId,
        chunk_index: payload.index,
        error_type: payload.errorCode,
        reason: payload.message,
        session_source_type: SESSION_SOURCE_TYPE.LocalRecording,
      },
      'warn'
    );
  }

  // Auth failure: fail visibly; do not log the user out.
  if (payload.errorCode === 'ONLINE_SESSION_AUTH') {
    showAuthFailureToast();
  }
}

/**
 * Subscribe to the `online_chunk:*` events for the given session. Idempotent:
 * any previously-attached listeners are detached first.
 */
export async function attachOnlineChunkUploadListeners(
  sessionId: string,
  rustAuth0TokenRefresh: boolean,
  useTranscriptionServiceV2 = false
): Promise<void> {
  detachOnlineChunkUploadListeners();
  rustAuth0TokenRefreshLock = rustAuth0TokenRefresh;
  activeSessionId = sessionId;

  const unlistens = await Promise.all([
    listenOnlineChunkUploaded((event) => {
      handleChunkUploaded(event.payload);
      void pollLiveTranscripts(sessionId);
      if (
        useTranscriptionServiceV2 &&
        (isNoSpeechWarningStartupEnabled() || isNoSpeechWarningMidSessionEnabled())
      ) {
        void reactQueryClient.invalidateQueries({
          queryKey: [TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY, sessionId],
        });
      }
    }),
    listenOnlineChunkFailed((event) => {
      handleChunkFailed(event.payload);
    }),
    listenOnlineChunkTokenRefreshNeeded(() => {
      void pushFreshOnlineChunkToken();
    }),
  ]);

  activeDetach = () => {
    unlistens.forEach((unlisten) => unlisten());
  };
}

/** Tear down the online chunk-upload listeners (no-op if none attached). */
export function detachOnlineChunkUploadListeners(): void {
  if (activeDetach) {
    activeDetach();
    activeDetach = null;
  }
  rustAuth0TokenRefreshLock = false;
  activeSessionId = null;
}
